import * as L from 'leaflet'
import * as geojson from 'geojson'
import * as turf from '@turf/turf'
import * as utils from './utils'
import { type GeoLocation } from '../tcm_service'
import FilterManager from '../tcm_filter_manager'

import BaseSelectionModeBehaviour from './selection_mode_behaviour'

type TurfFeature = geojson.Feature<geojson.Polygon | geojson.MultiPolygon>

export default class RouteSelectionModeBehaviour extends BaseSelectionModeBehaviour {
    private locationList: GeoLocation[] = []
    private areaPolygon: TurfFeature | null = null

    override onBeforeScopeUpdate(): void {
        this.scope.slider.visible = true
    }

    override applyFilter(mgr: FilterManager): void {
        mgr.localizacaoRoutes.clear()

        if (this.areaPolygon && this.areaPolygon.geometry.coordinates.length > 0) {
            this.areaPolygon.geometry.coordinates.forEach((coordinate) => {
                const entry: GeoLocation[] = coordinate.map((v) => ({
                    lat: v[1] as number,
                    lon: v[0] as number
                }))

                mgr.localizacaoRoutes.add(entry)
            })
        }
    }

    plotFromFilter(map: L.Map, mgr: FilterManager): void {
        this.removeAllLayers()
        this.locationList.length = 0

        const multiPolygons: geojson.Position[][] = []

        for (const item of mgr.localizacaoRoutes.values()) {
            multiPolygons.push(item.map(v => ([v.lon, v.lat])))

            const centralPoints = calculateCenterLines(item)

            for (const point of centralPoints.centerLinePoints) {
                this.locationList.push(point)
            }
        }

        this.areaPolygon = turf.polygon(multiPolygons) as TurfFeature
        this.doDraw(map)
        this.scope.update()
    }

    //eslint-disable-next-line @typescript-eslint/no-unused-vars
    override setZoom(_value: number): void {
        // TODO
    }

    override setMode(value: number): void {
        const scope = this.scope
        scope.mode = 'route'
        scope.slider.unit = 'm'
        scope.slider.min = 10
        scope.slider.max = 1000
        scope.slider.step = 10
        scope.slider.value = 50
        this.setZoom(value)
    }

    override clear() {
        super.clear()
        this.locationList.length = 0
        this.areaPolygon = null
        this.owner.filterManager.localizacaoRoutes.clear()
    }

    override hasFilter() {
        return this.locationList.length > 0
    }

    addLocation(location: GeoLocation) {
        this.locationList.push(location)
        this.scope.update()
    }

    preparePoints() {
        const offset = turf.lengthToDegrees(this.getDistanceInMeters() / 2, 'meters')
        this.doPreparePoints(offset)
    }

    private doPreparePoints(offset: number) {
        this.areaPolygon = null
        if (this.locationList.length <= 1) {
            return
        }

        const multiPolygons: L.LatLngLiteral[][] = []

        let pA: GeoLocation | null = null
        let pB: GeoLocation | null = null
        let p1 = this.locationList[0]
        for (let i = 1; i < this.locationList.length; i++) {
            const p2 = this.locationList[i]
            const [p3, p4] = utils.computeLine(p1, p2, offset)
            const [p5, p6] = utils.computeLine(p1, p2, -offset)

            if (!pA) {
                pA = p3
            }

            if (!pB) {
                pB = p5
            }

            const points = turf.featureCollection([
                turf.point([pA.lon, pA.lat]),
                turf.point([p4.lon, p4.lat]),
                turf.point([pB.lon, pB.lat]),
                turf.point([p6.lon, p6.lat]),
                turf.point([pA.lon, pA.lat])
            ])

            const hull = turf.convex(points)
            if (!hull) {
                continue
            }

            hull.geometry.coordinates.forEach((coordinate) => {
                const mapPolygon: L.LatLngLiteral[] = []

                coordinate.forEach((v) => {
                    mapPolygon.push({
                        lat: v[1] as number,
                        lng: v[0] as number
                    })
                })

                multiPolygons.push(mapPolygon)
            })

            p1 = p2
            pA = p4
            pB = p6
        }

        this.areaPolygon = null
        const area = multiPolygons[0]
        if (area) {
            let areaPolygon = turf.polygon([area.map((v) => [v.lng, v.lat])]) as TurfFeature

            for (let i = 1; i < multiPolygons.length; i++) {
                const curAreaPolygon = turf.polygon([multiPolygons[i].map((v) => [v.lng, v.lat])])
                const unitedPolygon = turf.union(turf.featureCollection([areaPolygon, curAreaPolygon]))
                if (unitedPolygon) {
                    areaPolygon = unitedPolygon
                }
            }

            this.areaPolygon = areaPolygon
        }
    }

    doDraw(map: L.Map) {
        if (this.locationList.length === 1) {
            const v = this.locationList[0]
            const radiusInMeters = this.getDistanceInMeters() / 2
            const circleLayer = L.circle([v.lat, v.lon], { radius: radiusInMeters }).addTo(map)
            this.removeAllLayers = () => {
                circleLayer.removeFrom(map)
                this.removeAllLayers = utils.staticNoopOther
            }
            return
        }

        const multiPolygons: L.LatLngLiteral[][] = []
        if (this.areaPolygon) {
            this.areaPolygon.geometry.coordinates.forEach((coordinate) => {
                const entry: L.LatLngLiteral[] = []
                coordinate.forEach((point) => {
                    entry.push({
                        lat: point[1] as number,
                        lng: point[0] as number
                    })
                })
                multiPolygons.push(entry)
            })
        }

        if (multiPolygons.length > 0) {
            const areaLayer = L.polygon(multiPolygons).addTo(map)
            this.removeAllLayers = () => {
                areaLayer.removeFrom(map)
                this.removeAllLayers = utils.staticNoopOther
            }
        }
    }
}

//
// Algoritmo para determinar a reta central
//

// Tipo para representar um retângulo (4 pontos)
type Rectangle = [GeoLocation, GeoLocation, GeoLocation, GeoLocation]

// Tipo para o resultado da função
interface CenterLinesResult {
    centerLinePoints: GeoLocation[] // Pontos das retas centrais
    averageWidth: number // Largura média dos retângulos
}

// Função principal para calcular as retas centrais e a largura média
function calculateCenterLines(points: GeoLocation[]): CenterLinesResult {
    // Passo 1: Identificar os retângulos a partir dos pontos
    const rectangles = identifyRectangles(points)

    const centerPoints: GeoLocation[] = []
    const widths: number[] = [] // Armazena as larguras dos retângulos

    // Passo 2: Processar cada retângulo
    for (const rectangle of rectangles) {
        // Passo 2.1: Calcular o centro do retângulo
        const center = calculateRectangleCenter(rectangle)

        // Passo 2.2: Determinar a orientação da reta central e a largura
        const { direction, length, width } = getRectangleOrientation(rectangle)

        // Armazenar a largura
        widths.push(width)

        // Passo 2.3: Gerar pontos da reta central
        const halfLength = length / 2
        const point1 = {
            lat: center.lat - halfLength * direction.lat,
            lon: center.lon - halfLength * direction.lon
        }
        const point2 = {
            lat: center.lat + halfLength * direction.lat,
            lon: center.lon + halfLength * direction.lon
        }

        // Adicionar pontos à lista (início, centro, fim da reta central)
        centerPoints.push(point1, center, point2)
    }

    // Passo 3: Conectar os pontos centrais (simplificado: retorna pontos ordenados)
    const connectedPoints = connectCenters(centerPoints, rectangles)

    // Passo 4: Calcular a largura média
    const averageWidth = widths.length > 0 ? widths.reduce((sum, w) => sum + w, 0) / widths.length : 0

    return {
        centerLinePoints: connectedPoints,
        averageWidth
    }
}

// Função para identificar retângulos a partir dos pontos
function identifyRectangles(points: GeoLocation[]): Rectangle[] {
    const rectangles: Rectangle[] = []

    // Simplificação: Assume que os pontos formam retângulos consecutivos
    // Cada retângulo é definido por 4 pontos consecutivos
    for (let i = 0; i < points.length - 3; i += 2) {
        const rectPoints = [points[i], points[i + 1], points[i + 2], points[i + 3]]
        if (isRectangle(rectPoints)) {
            rectangles.push(rectPoints as Rectangle)
        }
    }

    return rectangles
}

// Função para verificar se 4 pontos formam um retângulo
function isRectangle(points: GeoLocation[]): boolean {
    if (points.length !== 4) return false

    const distances = [
        distance(points[0], points[1]),
        distance(points[1], points[2]),
        distance(points[2], points[3]),
        distance(points[3], points[0])
    ]

    const isOppositeEqual =
        Math.abs(distances[0] - distances[2]) < 0.0001 && Math.abs(distances[1] - distances[3]) < 0.0001

    const vector1 = { lat: points[1].lat - points[0].lat, lon: points[1].lon - points[0].lon }
    const vector2 = { lat: points[2].lat - points[1].lat, lon: points[2].lon - points[1].lon }
    const isPerpendicular = Math.abs(vector1.lat * vector2.lat + vector1.lon * vector2.lon) < 0.0001

    return isOppositeEqual && isPerpendicular
}

// Função para calcular a distância entre dois pontos
function distance(p1: GeoLocation, p2: GeoLocation): number {
    return Math.sqrt((p2.lat - p1.lat) ** 2 + (p2.lon - p1.lon) ** 2)
}

// Função para calcular o centro de um retângulo
function calculateRectangleCenter(rectangle: Rectangle): GeoLocation {
    const [p1, p2, p3, p4] = rectangle
    return {
        lat: (p1.lat + p2.lat + p3.lat + p4.lat) / 4,
        lon: (p1.lon + p2.lon + p3.lon + p4.lon) / 4
    }
}

// Função para determinar a orientação, comprimento e largura do retângulo
function getRectangleOrientation(rectangle: Rectangle): { direction: GeoLocation; length: number; width: number } {
    const [p1, p2, p3] = rectangle

    // Calcular comprimentos dos lados
    const side1Length = distance(p1, p2)
    const side2Length = distance(p2, p3)

    // O lado mais longo define a direção da reta central, o mais curto é a largura
    if (side1Length > side2Length) {
        const dx = p2.lat - p1.lat
        const dy = p2.lon - p1.lon
        const magnitude = Math.sqrt(dx ** 2 + dy ** 2)
        return {
            direction: { lat: dx / magnitude, lon: dy / magnitude },
            length: side1Length,
            width: side2Length
        }
    } else {
        const dx = p3.lat - p2.lat
        const dy = p3.lon - p2.lon
        const magnitude = Math.sqrt(dx ** 2 + dy ** 2)
        return {
            direction: { lat: dx / magnitude, lon: dy / magnitude },
            length: side2Length,
            width: side1Length
        }
    }
}

// Função para conectar os pontos centrais
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function connectCenters(centerPoints: GeoLocation[], _rectangles: Rectangle[]): GeoLocation[] {
    // Simplificação: Retorna os pontos na ordem gerada
    return centerPoints
}
